package com.fantasytenis.shared.data.bracket.api

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.core.network.GenericErrorResponse
import com.fantasytenis.shared.core.network.WrappedResponse
import com.fantasytenis.shared.core.network.lenientJson
import com.fantasytenis.shared.data.bracket.dto.*
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*

/**
 * Bracket API client.
 *
 * Handles all bracket challenge-related HTTP requests.
 */
class BracketApi(
    private val httpClient: HttpClient,
    private val baseUrl: String
) {

    // ===== TOURNAMENT DRAWS =====

    /**
     * Get a tournament draw by ID.
     * GET /v2/draws/:id
     */
    suspend fun getTournamentDraw(drawId: String): ApiResponse<TournamentDrawDto> {
        return try {
            val response = httpClient.get("${baseUrl}draws/$drawId")

            if (response.status.isSuccess()) {
                val wrapped = response.body<BracketApiResponse<TournamentDrawDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Draw not found", response.status.value)
                }
            } else {
                val wrapped = response.body<BracketApiResponse<TournamentDrawDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to get draw", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * List tournament draws.
     * GET /v2/draws
     */
    suspend fun listTournamentDraws(
        status: Int? = null,
        gender: String? = null,
        limit: Int = 20,
        offset: Int = 0
    ): ApiResponse<List<TournamentDrawDto>> {
        return try {
            val url = buildString {
                append("${baseUrl}draws?limit=$limit&offset=$offset")
                status?.let { append("&status=$it") }
                gender?.let { append("&gender=$it") }
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<BracketListResponse<TournamentDrawDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to list draws", response.status.value)
                }
            } else {
                val wrapped = response.body<BracketListResponse<TournamentDrawDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to list draws", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get draw positions.
     * GET /v2/draws/:id/positions
     */
    suspend fun getDrawPositions(drawId: String, round: Int? = null): ApiResponse<List<DrawPositionDto>> {
        return try {
            val url = buildString {
                append("${baseUrl}draws/$drawId/positions")
                round?.let { append("?round=$it") }
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<BracketListResponse<DrawPositionDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to get positions", response.status.value)
                }
            } else {
                val wrapped = response.body<BracketListResponse<DrawPositionDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to get positions", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== BRACKET CHALLENGES =====

    /**
     * Get a bracket challenge by ID.
     * GET /v2/bracket-challenges/:id
     */
    suspend fun getBracketChallenge(challengeId: String): ApiResponse<BracketChallengeDto> {
        return try {
            val response = httpClient.get("${baseUrl}bracket-challenges/$challengeId")

            if (response.status.isSuccess()) {
                val wrapped = response.body<BracketApiResponse<BracketChallengeDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Challenge not found", response.status.value)
                }
            } else {
                val wrapped = response.body<BracketApiResponse<BracketChallengeDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to get challenge", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * List bracket challenges.
     * GET /v2/bracket-challenges
     */
    suspend fun listBracketChallenges(
        status: Int? = null,
        featuredOnly: Boolean = false,
        limit: Int = 20,
        offset: Int = 0
    ): ApiResponse<List<BracketChallengeDto>> {
        return try {
            val url = buildString {
                append("${baseUrl}bracket-challenges?limit=$limit&offset=$offset")
                status?.let { append("&status=$it") }
                if (featuredOnly) append("&featuredOnly=true")
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<BracketListResponse<BracketChallengeDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to list challenges", response.status.value)
                }
            } else {
                val wrapped = response.body<BracketListResponse<BracketChallengeDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to list challenges", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== BRACKET ENTRIES =====

    /**
     * Create a bracket entry (join a challenge).
     * POST /v2/bracket-challenges/:id/enter
     */
    suspend fun createBracketEntry(
        challengeId: String,
        request: CreateBracketEntryRequest
    ): ApiResponse<BracketEntryDto> {
        return try {
            val response = httpClient.post("${baseUrl}bracket-challenges/$challengeId/enter") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            // Backend returns HTTP 200 even for errors, with actual status in body.
            // First parse just status/error to check for errors (avoids data:[] parsing issue)
            val bodyText = response.bodyAsText()
            val errorCheck = lenientJson.decodeFromString<GenericErrorResponse>(bodyText)

            if (errorCheck.isSuccess) {
                // Success - parse full response with data
                val wrapped = lenientJson.decodeFromString<BracketApiResponse<BracketEntryDto>>(bodyText)
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to create entry", wrapped.status)
                }
            } else {
                // Error response - use the error from errorCheck (avoids parsing data:[])
                ApiResponse.Error(errorCheck.error ?: "Failed to create entry", errorCheck.status)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get a bracket entry with picks.
     * GET /v2/bracket-entries/:id
     */
    suspend fun getBracketEntry(entryId: String): ApiResponse<BracketEntryWithPicksResponse> {
        return try {
            val response = httpClient.get("${baseUrl}bracket-entries/$entryId")

            if (response.status.isSuccess()) {
                val result = response.body<BracketEntryWithPicksResponse>()
                if (result.data != null) {
                    ApiResponse.Success(result)
                } else {
                    ApiResponse.Error(result.error ?: "Entry not found", response.status.value)
                }
            } else {
                val result = response.body<BracketEntryWithPicksResponse>()
                ApiResponse.Error(result.error ?: "Failed to get entry", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get user's bracket entries.
     * GET /v2/users/me/bracket-entries
     */
    suspend fun getUserBracketEntries(challengeId: String? = null): ApiResponse<List<BracketEntryDto>> {
        return try {
            val url = buildString {
                append("${baseUrl}users/me/bracket-entries")
                challengeId?.let { append("?challengeId=$it") }
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<BracketListResponse<BracketEntryDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to get entries", response.status.value)
                }
            } else {
                val wrapped = response.body<BracketListResponse<BracketEntryDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to get entries", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Submit bracket picks.
     * POST /v2/bracket-entries/:id/picks
     */
    suspend fun submitBracketPicks(
        entryId: String,
        request: SubmitBracketPicksRequest
    ): ApiResponse<BracketEntryDto> {
        return try {
            val response = httpClient.post("${baseUrl}bracket-entries/$entryId/picks") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            // Backend returns HTTP 200 even for errors, check body status first
            val bodyText = response.bodyAsText()
            val errorCheck = lenientJson.decodeFromString<GenericErrorResponse>(bodyText)

            if (errorCheck.isSuccess) {
                val wrapped = lenientJson.decodeFromString<SubmitPicksResponseDto>(bodyText)
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to submit picks", wrapped.status)
                }
            } else {
                ApiResponse.Error(errorCheck.error ?: "Failed to submit picks", errorCheck.status)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Cancel a bracket entry.
     * DELETE /v2/bracket-entries/:id
     */
    suspend fun cancelBracketEntry(entryId: String): ApiResponse<BracketEntryDto> {
        return try {
            val response = httpClient.delete("${baseUrl}bracket-entries/$entryId")

            // Backend returns HTTP 200 even for errors, check body status first
            val bodyText = response.bodyAsText()
            val errorCheck = lenientJson.decodeFromString<GenericErrorResponse>(bodyText)

            if (errorCheck.isSuccess) {
                val wrapped = lenientJson.decodeFromString<BracketApiResponse<BracketEntryDto>>(bodyText)
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to cancel entry", wrapped.status)
                }
            } else {
                ApiResponse.Error(errorCheck.error ?: "Failed to cancel entry", errorCheck.status)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== LEADERBOARD =====

    /**
     * Get bracket challenge leaderboard.
     * GET /v2/bracket-challenges/:id/leaderboard
     */
    suspend fun getBracketLeaderboard(
        challengeId: String,
        limit: Int = 100,
        offset: Int = 0
    ): ApiResponse<List<BracketEntryDto>> {
        return try {
            val url = "${baseUrl}bracket-challenges/$challengeId/leaderboard?limit=$limit&offset=$offset"

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<BracketListResponse<BracketEntryDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to get leaderboard", response.status.value)
                }
            } else {
                val wrapped = response.body<BracketListResponse<BracketEntryDto>>()
                ApiResponse.Error(wrapped.error ?: "Failed to get leaderboard", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== ADMIN: LOCK OVERRIDE =====

    /**
     * Set lock override for a bracket challenge (SuperAdmin only).
     * PUT /v2/admin/bracket-challenges/:id/lock-override
     */
    suspend fun setLockOverride(
        challengeId: String,
        request: SetLockOverrideRequest
    ): ApiResponse<BracketChallengeDto> {
        return try {
            val response = httpClient.put("${baseUrl}admin/bracket-challenges/$challengeId/lock-override") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<SetLockOverrideResponse>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to set lock override", response.status.value)
                }
            } else {
                val wrapped = response.body<SetLockOverrideResponse>()
                ApiResponse.Error(wrapped.error ?: "Failed to set lock override", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    // ===== ADMIN: DELETE ENTRY =====

    /**
     * Admin delete a bracket entry (SuperAdmin only).
     * DELETE /v2/admin/bracket-entries/:entryId
     */
    suspend fun adminDeleteBracketEntry(
        entryId: String,
        request: AdminDeleteBracketEntryRequest
    ): ApiResponse<AdminDeleteBracketEntryResponse> {
        return try {
            val response = httpClient.delete("${baseUrl}admin/bracket-entries/$entryId") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<AdminDeleteBracketEntryResponse>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped)
                } else {
                    ApiResponse.Error(wrapped.error ?: "Failed to delete entry", response.status.value)
                }
            } else {
                val wrapped = response.body<AdminDeleteBracketEntryResponse>()
                ApiResponse.Error(wrapped.error ?: "Failed to delete entry", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }
}
